import api from "../axiosIntercepture";
import { assertApiSuccess, getApiErrorMessage } from "../../utils/apiErrorMessage";
import { getSerialConfig } from "../../utils/serialCapture";

const normalizeInventoryFilters = (filters = []) => {
    if (!Array.isArray(filters)) return [];
    return filters
        .filter((filter) => filter && filter.columnId && filter.value != null && String(filter.value).trim() !== "")
        .map((filter) => ({
            columnId: filter.columnId,
            operator: filter.operator || "contains",
            value: filter.value,
        }));
};

export const fetchItemInventory = async (page, pageSize, filters = [], cat) => {
    try {
        const catQuery = cat ? `&cat=${encodeURIComponent(cat)}` : "";
        const response = await api.post(
            `item/getItemInventory?limit=${pageSize}&page=${page + 1}${catQuery}`,
            { filters: normalizeInventoryFilters(filters) }
        );
        return response.data?.results;
    } catch (err) {
        throw err?.response?.data?.message;
    }
}

export const refreshWMSAPI  = async () => {
    try {
        const response = await api.post('/item/refresh-wms', {});
        return response?.data;
    }
    catch (err) {
        throw err?.response?.data || err;
    }
}


export const fetchSkuMaster = async (page, pageSize, filters = []) => {
    try {
        const response = await api.post(
            `item/getAllItems?limit=${pageSize}&page=${page + 1}`,
            { filters: Array.isArray(filters) ? filters : [] }
        );
        return response.data?.results;
    } catch (err) {
        throw err?.response?.data?.message;
    }
}


const normalizeLedgerFilters = (filters = []) => {
    if (!Array.isArray(filters)) return [];
    return filters
        .filter((filter) => filter && filter.columnId && filter.value != null && String(filter.value).trim() !== "")
        .map((filter) => ({
            columnId: filter.columnId,
            operator: filter.operator || "contains",
            value: filter.value,
        }));
};

export const getInventoryLedger = async (startDate, endDate, pageNo, pageSizeNo, filters = []) => {
    try {
        const sendObj = {
            startDate,
            endDate,
            page: pageNo + 1,
            limit: pageSizeNo,
            filters: normalizeLedgerFilters(filters),
        };
        const response = await api.post("item/getInventoryLedger", sendObj);
        assertApiSuccess(response, "Failed to load inventory ledger");

        const body = response.data;
        const results = body?.results ?? body?.data ?? body;
        if (Array.isArray(results)) {
            return { data: results, totalItems: results.length };
        }

        const data = results?.data ?? results?.list ?? results?.rows ?? [];
        const totalItems =
            results?.totalItems ??
            results?.totalRecords ??
            results?.total ??
            body?.totalItems ??
            (Array.isArray(data) ? data.length : 0);

        return {
            data: Array.isArray(data) ? data : [],
            totalItems,
        };
    } catch (err) {
        throw getApiErrorMessage(err, "Failed to load inventory ledger");
    }
};


export async function fetchDataAndPatchFields(fieldName, fieldValue, organizationCustomerId) {
    try {
        console.log( organizationCustomerId);
        
        const sendObj = {
            skuId: fieldValue,
            ...(organizationCustomerId ? { organizationCustomerId } : {}),
        };
        const response = await api.post("item/getSkuDetailsById", sendObj);

        if (response.data?.results) {

            const dataRd = response.data?.results;

            if (dataRd.length > 0) {
                const row = dataRd[0];
                const originalPrice =
                    row.originalPrice ??
                    row.customerPricing?.originalPrice ??
                    row.header?.originalPrice ??
                    "";
                return {
                    skuId: row.header.skuId,
                    description: row.header.description,
                    itemCube: row.header.cube,
                    itemWeight: row.header.netWeight,
                    pack: row.header.pack,
                    uom: row.header.uom,
                    unitPrice:
                        originalPrice !== "" && originalPrice != null
                            ? String(originalPrice)
                            : "",
                    serialConfig: getSerialConfig(row),
                };

            } else {
                alert(`No data found for ${fieldName}: ${fieldValue}`);
                return null;
            }
        }

    } catch (error) {
        console.error("Error fetching data:", error);
        return null;
    }
}


export async function fetchDataAndPatchFieldForSo(fieldName, fieldValue, organizationCustomerId) {
    try {
        console.log(fieldName , fieldValue);
        
        const sendObj = {
            skuId: fieldValue,
            ...(organizationCustomerId ? { organizationCustomerId } : {}),
        };
        const response = await api.post("item/getSkuDetailsById", sendObj);

        if (response.data?.results) {

            const dataRd = response.data?.results;

            if (dataRd.length > 0) {
                const row = dataRd[0];
                const selling =
                    row.sellingPrice ??
                    row.customerPricing?.sellingPrice ??
                    row.header?.sellingPrice ??
                    "";
                return {
                    item: row.header.skuId,
                    description: row.header.description,
                    itemcode: row.header.cube,
                    itemweight: row.header.netWeight,
                    pack: row.header.pack,
                    uom: row.header.uom,
                    itemPerPrice:
                        selling !== "" && selling != null ? String(selling) : "",
                    serialConfig: getSerialConfig(row),
                };

            } else {
                alert(`No data found for ${fieldName}: ${fieldValue}`);
                return null;
            }
        }

    } catch (error) {
        console.error("Error fetching data:", error);
        return null;
    }
}

function looksLikeSkuDetail(value) {
    if (!value || typeof value !== "object" || Array.isArray(value)) return false;
    if (value.weightData && typeof value.weightData === "object") return true;
    const header = value.header;
    return !!(header && typeof header === "object" && (header.skuId || header.owner || header.description));
}

function unwrapSkuDetail(body) {
    const seen = new Set();
    const visit = (value, depth) => {
        if (!value || typeof value !== "object" || depth > 6 || seen.has(value)) return null;
        seen.add(value);
        if (Array.isArray(value)) {
            for (const item of value) {
                const found = visit(item, depth + 1);
                if (found) return found;
            }
            return null;
        }
        if (looksLikeSkuDetail(value)) return value;
        for (const key of ["results", "data", "list", "item", "record", "content"]) {
            const found = visit(value[key], depth + 1);
            if (found) return found;
        }
        return null;
    };
    return visit(body, 0);
}

export const getSkuDetailsById = async (skuId, organizationCustomerId) => {
    try {
        const sendObj = {
            skuId,
            ...(organizationCustomerId ? { organizationCustomerId } : {}),
        };
        const response = await api.post("item/getSkuDetailsById", sendObj);
        return unwrapSkuDetail(response.data);
    } catch (err) {
        throw getApiErrorMessage(err, "Failed to load SKU details");
    }
};